import { trackEvent } from '@patrianna/analytics'
import { getTranslationFromJson } from '@patrianna/shared-utils/prismic'
import { useState } from 'react'
import type { Dispatch, SetStateAction } from 'react'

import { PostalCodeStepsEnum } from 'pageContainers/PostalCodePage/types'
import { serviceDependencies } from 'src/services'
import { useAppDispatch } from 'src/store/hooks'
import type { TypedDispatch } from 'src/store/types'
import { openSnackbar } from 'store/modules/snackbar/actions'
import type {
  CreatePostalCodeRequest as RestCreatePostalCodeRequest,
  CreatePostalCodeResponse as RestCreatePostalCodeResponse,
  RevealPostalCodeRequest,
  RevealPostalCodeResponse,
} from 'temp/api/client'

import content from '../../../prismic-translates/dialog-sc-request-code.json'

const t = getTranslationFromJson([content])

const trackCodeGenerationEvent = () => {
  trackEvent(
    'code_generated',
    {
      category: 'CreatePostalCodeResponse',
      label: 'sweepstakesPostalCodeGenerated',
    },
    {
      bloomreach: false,
    },
  )
}

const SCRequest = ({
  recaptchaToken,
  dispatch,
  brandName,
  setRevealToken,
  setSCCode,
  setStep,
}: {
  recaptchaToken: string
  dispatch: TypedDispatch
  brandName: string
  setRevealToken: Dispatch<SetStateAction<string>>
  setSCCode: Dispatch<SetStateAction<string>>
  setStep: Dispatch<SetStateAction<PostalCodeStepsEnum>>
}) => {
  const data: RestCreatePostalCodeRequest = {
    recaptchaToken,
    brandName,
  }

  return serviceDependencies.api
    .createPostalCode(data)
    .then((body: RestCreatePostalCodeResponse) => {
      if (body?.revealFlowEnabled) {
        setRevealToken(body?.revealToken)
        setStep(PostalCodeStepsEnum.SCRATCH_STEP_3)
      } else {
        trackCodeGenerationEvent()
        setSCCode(body?.postalCode)
        setStep(PostalCodeStepsEnum.RECEIVED_CODE_STEP_4)
      }
    })
    .catch((err) => {
      dispatch(serviceDependencies.errorHandler(err, data))

      throw err
    })
}

const SCRequestWithRevealToken = ({
  dispatch,
  revealToken,
  setStep,
  setSCCode,
}: {
  dispatch: TypedDispatch
  revealToken: string
  setStep: Dispatch<SetStateAction<PostalCodeStepsEnum>>
  setSCCode: Dispatch<SetStateAction<string>>
}) => {
  const data: RevealPostalCodeRequest = {
    revealToken,
  }

  return serviceDependencies.api
    .revealPostalCode(data)
    .then((body: RevealPostalCodeResponse) => {
      setSCCode(body?.postalCode)

      if (body?.postalCode) {
        setStep(PostalCodeStepsEnum.RECEIVED_CODE_STEP_4)
      }
    })
    .catch((err) => {
      if (err) {
        setStep(PostalCodeStepsEnum.RECAPCHA_STEP_2)
        dispatch(openSnackbar({ message: t('failed_to_generate_code') }))
      }

      dispatch(serviceDependencies.errorHandler(err, data))
    })
}

export const useSCRequest = () => {
  const dispatch = useAppDispatch()

  const [SCCode, setSCCode] = useState('')
  const [isSCLoading, setIsSCLoading] = useState(true)
  const [revealToken, setRevealToken] = useState('')

  const setRecaptchaToken = (recaptchaToken: string, setStep: Dispatch<SetStateAction<PostalCodeStepsEnum>>) => {
    SCRequest({
      recaptchaToken,
      dispatch,
      brandName: process.env.BRAND,
      setRevealToken,
      setSCCode,
      setStep,
    }).finally(() => setIsSCLoading(false))
  }

  const requestSCTokenWithRevealToken = (setStep: Dispatch<SetStateAction<PostalCodeStepsEnum>>) => {
    setIsSCLoading(true)
    SCRequestWithRevealToken({ dispatch, revealToken, setStep, setSCCode }).finally(() => setIsSCLoading(false))
  }

  return { SCCode, isSCLoading, setRecaptchaToken, requestSCTokenWithRevealToken }
}
